/**
 * Human-readable output for the terminal.
 *
 * Everything here is plain text with no colour codes, so piping to a file or a log
 * produces exactly what appeared on screen.
 */

import type { Diagnostic } from '../edf/errors.js';
import type { EdfFile } from '../edf/reader.js';
import { describeFormat, formatRates, formatWallClock } from '../edf/header.js';
import { fixed, formatBytes, formatDuration, plainSeconds } from '../format/number.js';
import { counted, grouped } from '../format/list.js';
import { escapeJsonText, printable } from '../format/unprintable.js';
import type { ConversionPlan } from '../convert/plan.js';
import { outputCsvName } from '../convert/plan.js';
import type { ConvertResult } from '../convert/run.js';
import { VERSION } from '../version.js';

/** Where terminal prose wraps. The width --help is written to, and the ANSI default. */
const WRAP_COLUMNS = 80;

/** The continuation indent under a `warning: ` prefix, which is the only one there is. */
const HINT_INDENT = ' '.repeat(9);

/**
 * One unbreakable unit of a wrapped line: a quoted span, or a run of non-space.
 *
 * The lookbehind is what keeps an apostrophe out of it. `the file's own header and it's` has
 * two of them, and without the guard `'s own header and it'` is one quoted span — a token
 * with spaces in it that then refuses to break. A quote opening a span is at the start of a
 * word, so that is what is required of it.
 */
const WORD = /(?<=^|[\s(])(?:"[^"]*"|'[^']*')|\S+/gu;

/**
 * Greedy word wrap, `indent` on every line including the first.
 *
 * Only free prose goes through this. The aligned parts of `--info` — the `Format`/`Size`
 * key-value lines and the channel table — are laid out in columns, and re-flowing a column
 * is how you turn a table into a paragraph.
 *
 * A word wider than the column is left to overrun rather than broken. The long words here
 * are file paths and quoted channel labels, and neither survives being split across lines:
 * the point of printing a path is that it can be copied back out.
 *
 * A quoted span is one word for the same reason, however many spaces are inside it. The
 * sentence above is the promise, and it held only for the paths that have no spaces in them:
 * an interrupted conversion said `Files already written to "/tmp/a very long destination
 * folder name with` / `many spaces here indeed" are incomplete`, splitting the one thing on
 * the line that has to be copied whole — and quoting is what a path with spaces in it is
 * given in the first place. A word wider than the column already overruns rather than
 * breaking; this is that rule reaching the words it was written for.
 *
 * Which is also why the gap between two words is reproduced rather than normalised to one
 * space. Splitting on `\s+` and rejoining with `' '` re-flowed everything, and a quoted value
 * is not prose: a channel labelled `EEG  A` was offered back as `Did you mean "EEG A"?`, and
 * a destination given as `-my  nightly` as `Write it as one argument instead:
 * '--out=-my nightly'`. Following either gets a different channel or a different directory
 * than the one the sentence is about, and the run before it had already quoted the name
 * correctly on the line above.
 */
export function wrap(text: string, indent = '', width = WRAP_COLUMNS): string {
  const lines: string[] = [];
  let line = indent;
  let at = 0;
  for (const match of text.matchAll(WORD)) {
    const piece = match[0];
    const gap = text.slice(at, match.index);
    at = match.index + piece.length;
    if (line === indent) line += piece;
    else if (line.length + gap.length + piece.length <= width) line += gap + piece;
    else {
      lines.push(line);
      line = indent + piece;
    }
  }
  if (line !== indent) lines.push(line);
  return lines.join('\n');
}

function table(rows: readonly (readonly string[])[], alignRight: ReadonlySet<number>): string {
  if (rows.length === 0) return '';
  const width: number[] = [];
  for (const row of rows) {
    row.forEach((cell, i) => {
      width[i] = Math.max(width[i] ?? 0, cell.length);
    });
  }
  return rows
    .map((row) =>
      row
        .map((cell, i) => {
          const w = width[i] ?? 0;
          return alignRight.has(i) ? cell.padStart(w) : cell.padEnd(w);
        })
        .join('  ')
        .trimEnd(),
    )
    .join('\n');
}

/**
 * Make header text safe to print to a terminal.
 *
 * EDF identification fields and channel labels are free text copied verbatim out of the
 * file, and `--info` puts them straight on stdout. A header carrying ANSI escapes could
 * therefore drive the reader's terminal — `\x1b[2J\x1b[H` clears the screen and homes the
 * cursor, which is enough to hide the rest of the output or repaint it as something else.
 * Nobody writes an EDF header that way on purpose, which is exactly why a file that does
 * should not be trusted with the terminal.
 *
 * Control bytes are shown as their escape instead, so a corrupt field stays diagnosable
 * rather than being silently swallowed. This affects display only: `channels.csv` and
 * `metadata.json` still copy the field verbatim, and CSV quoting already makes that safe.
 */
// Defined in src/format/unprintable.ts, beside the character class it uses, since the library's
// own message builders need it too — see 0.8.92. Re-exported here, which is where every caller
// in this package already imports it from.
export { printable };

/**
 * The same protection for text that is meant to span lines.
 *
 * `printable` escapes newlines along with everything else, which is right for a channel
 * label — one has no business containing a line break, and it would break the `--info`
 * table's alignment. It is wrong for a whole message: several are written on two lines,
 * and Node's own option errors run to three. Escaping those turned the break into text:
 *
 *     error: No channel named "ECQ". Did you mean "ECG"?\x0aRun with --info to list ...
 *
 * Each line is escaped on its own, so nothing here gains the ability to drive a terminal.
 * A carriage return is still escaped, so no line can be repainted after it is printed —
 * which is the property that mattered. A newline can only add a line, never overwrite one.
 */
export function printableLines(text: string, indent = ''): string {
  return text
    .split('\n')
    .map((line, index) => {
      const safe = printable(line);
      // The first line is the one that follows `error: ` or `warning: `, so it is the line
      // a log gets grepped for, and it stays whole at whatever width it runs to.
      if (index === 0) return safe;

      /*
        Callers write the continuation indent one of two ways: most pass it here and leave
        their message lines flush, but the two usage builders bake `       ` into the string
        itself because they are printed without an `error: ` prefix in front. Reading the
        line's own leading space when none was passed keeps both working, and keeps the
        wrap aligned under the same column either way.
      */
      const body = safe.trimStart();
      const pad = indent === '' ? safe.slice(0, safe.length - body.length) : indent;

      /*
        A continuation that is a command is copied, not read.

        `edf2csv -- "--chanels"` is the line the unknown-option error ends on, and it exists
        to be pasted back into the shell. Wrapping puts `edf2csv --` on one line and the
        flag on the next, and what gets pasted is half a command. Prose survives being
        re-flowed and a command does not, so the two are told apart rather than being
        treated alike and hoping today's strings stay short.
      */
      if (body.startsWith('edf2csv ')) return pad + body;
      return wrap(body, pad);
    })
    .join('\n');
}

/** The `--info` view: what is in this recording, and what would converting it produce. */
export function formatInfo(
  file: EdfFile,
  plan: ConversionPlan,
  /**
   * How many events a conversion would write, when that is already known.
   *
   * Null when it is not. `--info` reads the whole annotation channel of a discontinuous file,
   * because that is where its record times are; a continuous one it reads only far enough to
   * find the origin. So the count is in hand for one of the two, and the line below said "How
   * many events there are cannot be told from the header" for both — true of the header, and
   * beside the point on a file whose events had just been read and counted.
   */
  events: number | null = null,
  /**
   * Whether the run would stream to stdout, in which case no file is written at all.
   *
   * The OUTPUT column is "Named as they will be written. --info is read to find out what a
   * run leaves behind, and a script that opens the name it was given must find a file there"
   * — and under `--stdout` it named `signals.csv`, or `signals.csv.gz`, for a run that
   * creates no directory and no file. The same table already tells a channel that will not
   * be converted from one the file gives nothing to convert; this is the third thing it
   * could not say.
   */
  toStdout = false,
  /**
   * Whether `--stdout` would refuse the run this describes, in which case it writes nothing.
   *
   * The paragraph above the estimate already says so for the commonest of the three
   * refusals — "this recording makes 3 tables, one per rate — more than `--stdout` can
   * write" — and the line under it went on predicting the rows anyway:
   *
   *     Sampling rates differ, so this recording makes 3 tables, one per rate — more
   *     than --stdout can write. Converting into a directory writes one file each;
   *     --layout long puts them all in one table.
   *     Would write 14 rows, roughly 262 B.
   *
   *     warning: --stdout would refuse this run: needs exactly one table, ...
   *
   * Three statements about one command, and the middle one is of a run that exits 1 having
   * written nothing. The estimate is still the answer to a question worth asking — it is
   * what converting into a directory would write — so it is kept and its subject corrected,
   * which is the same repair `--annotations-only` got on this line at 0.4.51.
   *
   * Taken from the caller rather than worked out here: `stdoutRefusal` is the conversion's
   * own guard, and the warning below the report is already built from it, so there is one
   * answer rather than two that can drift.
   */
  stdoutRefused = false,
): string {
  const { header } = file;
  const lines: string[] = [];

  /*
    Escaped, like every other value that came out of the filesystem.

    A path is untrusted text: a folder may be named with an ESC byte, and a file name may
    hold a newline on every platform this runs on. The `[n/m]` header a batch prints has
    always escaped it and these two lines did not, so one line of a run reached the terminal
    as `study/esc\x1b[31mred.edf` and the next as a live colour change — and a name holding a
    newline split `Wrote` across two lines, so the summary reported a path that reads as two.
    NONPRINTABLE_LABEL exists because a header field can carry these bytes; a directory entry
    can carry them just as easily.
  */
  lines.push(`File       ${printable(file.path)}`);
  lines.push(`Format     ${describeFormat(header)}`);
  /*
    The two raw fields, with edges.

    Echoed bare, they ran into each other and into the parenthetical, and a header field is
    free to be empty or to be nothing but padding — `trimField` takes the padding off, so
    both of those arrive here as "". A file whose date field is blank printed

        Recorded    22.15.00 (unparseable)

    where the one value on the line is the *time* and nothing says so, and a file with both
    blank printed `Recorded     (unparseable)`: a label, a gap, and a verdict about nothing
    visible. `  .  .  ` in both fields came out `.  . .  .`, which is four fields or two
    depending on how the reader counts.

    Quoted, which is what `START_TIME_UNREADABLE` — the warning printed under this very line,
    about these very fields — has always done: `("" and "")`. Same argument 0.7.x made for the
    time-range refusals, in its words: without them "the value ran into the sentence ... and
    the surrounding spaces — the actual reason a shell-built argument went wrong — are
    invisible". `--info --json` has the two as separate strings and never had the problem.
  */
  lines.push(
    `Recorded   ${
      formatWallClock(header.startDateTime)?.replace('T', ' ') ??
      `"${printable(header.startDateRaw)}" "${printable(header.startTimeRaw)}" (unparseable)`
    }`,
  );
  lines.push(
    `Duration   ${formatDuration(file.durationSeconds)}  (${counted(file.recordCount, 'record')} of ${plainSeconds(header.recordDuration)}s)`,
  );
  const elapsedSpan = plan.range.recordingEndSeconds - plan.range.recordingStartSeconds;
  if (Math.abs(elapsedSpan - file.durationSeconds) > 1e-9) {
    /*
      Which way the two differ decides what to call it, and the parenthetical used to say
      "includes discontinuities" both ways round.

      A span LONGER than the duration is the gap case this line was written for: 3 records of
      1s covering 11 seconds. A span SHORTER than the duration cannot be a gap — it is records
      that overlap, which an EDF+D file gets when a device re-sends a buffer. Three records of
      1s starting at 0, 0.5 and 1 print:

          Duration   3s  (3 records of 1s)
          Time span  2s  (includes discontinuities)

      A recording covering less time than its own records account for, blamed on gaps it does
      not have — while the warning below it says, correctly, that two records overlap.

      A file holding both is described by whichever wins the subtraction, and the overlap
      warning is printed either way.
    */
    const overlapping = elapsedSpan < file.durationSeconds;
    lines.push(
      `Time span  ${formatDuration(elapsedSpan)}  ` +
        `(${overlapping ? 'records overlap in time' : 'includes discontinuities'})`,
    );
  }
  /*
    Where the samples begin, when that is not zero.

    0.4.9 made the first record's timekeeping TAL the point a recording is timed from, so a
    file whose TALs start at +1000 writes `time_s` from 1000.000 and takes `--start` and
    `--end` on that same clock. None of that appeared here: the report said "Duration 3s",
    which reads as 0 to 3, and `--start 0 --end 1` then selected nothing and answered with
    "The window is inside the recording but lands where there is no data ... Run with --info
    to see where the records actually sit" — pointing at this report, which was the one place
    the number was missing. It is in `plan.range` already and governs the estimate printed
    below; it was simply never shown.
  */
  const startsAt = plan.range.recordingStartSeconds;
  if (Number.isFinite(startsAt) && Math.abs(startsAt) > 1e-9) {
    // In seconds rather than through formatDuration, because this number is meant to be
    // typed back in: `--start` takes `1000s`, and "16m 40s" is not something it accepts.
    // It is also how the empty-window warning renders the window it was given.
    //
    // Through `fixed` rather than `toFixed`, which switches to exponent notation at 1e21 —
    // and `--start 1e+21s` is refused by the time parser with "uses an unknown unit \"e\"",
    // so the one line that says which clock to use handed back a number that clock rejects.
    // Reachable from a conforming file: an EDF+ onset is plain digits of any length, and a
    // record duration large enough to keep samples apart at that magnitude is four
    // characters. `fixed` expands these with BigInt, which is exact past 2^53 where a double
    // carries no fraction anyway, and is byte-for-byte `toFixed` everywhere else.
    lines.push(
      `Timed from ${fixed(startsAt, 3)}s  (first sample; --start and --end use this clock)`,
    );
  }
  /*
    And the window, when one was asked for, which this report did not mention at all.

    `--info` is the mode whose purpose is to say what a conversion will do, and every other
    flag that changes what gets written is visible in it: `--channels` puts `(not selected)`
    in the OUTPUT column, `--gzip` changes the names there, `--layout long` changes the
    sentence under the table, `--annotations-only` replaces the estimate outright. `--start`
    and `--end` changed one number:

        $ edf2csv rec.edf --info                    $ edf2csv rec.edf --info --start 1s --end 2s
        Duration   3s  (3 records of 1s)            Duration   3s  (3 records of 1s)
        ...                                         ...
        Would write 1,155 rows, roughly 22.2 KB.    Would write 385 rows, roughly 7.4 KB.

    Byte-for-byte identical otherwise — and the Duration line, which is what a reader checks a
    window against, goes on describing the whole file above an estimate that describes a third
    of it. The window is in `plan.range` already, which is the same thing `Timed from` above
    was added for: it governs the estimate printed below and was simply never shown.

    In seconds rather than through `formatDuration`, for the reason that line gives: these are
    numbers meant to be typed back into `--start` and `--end`, and "16m 40s" is not one.
  */
  const { range } = plan;
  /*
    An end that a double cannot hold is still an end.

    `endSeconds` is `recordCount * recordDuration` when no `--end` was given, and a header may
    state a record duration of 1e308 in five characters — two of those records overflow, so
    the figure comes back `Infinity`. This line required it to be finite and so vanished
    altogether on exactly the recording whose estimate is hardest to account for: the whole
    point of the line is to say why the row count changed, and `--start 1s` on such a file
    changed it from 8 rows to 7 with nothing on screen to say a window had been asked for.

    The window is still perfectly ordinary from this side — it begins where it was told to and
    runs to the end of the recording, which is what the line says now.
  */
  const endsAtInfinity = !Number.isFinite(range.endSeconds);
  if (!range.isWholeRecording && Number.isFinite(range.startSeconds)) {
    /*
      What the window selects, which is not data records in every mode.

      `--annotations-only` reads the annotation channel and converts no data records at all —
      the OUTPUT column of the table below says `(no signal data)` for every channel, and the
      line under it says "and no signal data". This counted them anyway:

          Window     1.000s to 3.000s  (2 of 3 data records)
          ...
          Would write annotations.csv and channels.csv, and no signal data.

      The window is not idle in that mode: it decides which events are exported, by onset. So
      the range stays and the parenthetical says what it is doing.
    */
    const selects = plan.writeSignals
      ? `${grouped(range.endRecord - range.startRecord)} of ${counted(file.recordCount, 'data record')}`
      : 'events outside it are not exported';
    const to = endsAtInfinity ? 'the end' : `${fixed(range.endSeconds, 3)}s`;
    lines.push(`Window     ${fixed(range.startSeconds, 3)}s to ${to}  (${selects})`);
  }
  lines.push(`Size       ${formatBytes(file.fileSize)}`);
  if (header.patientId) lines.push(`Patient    ${printable(header.patientId)}`);
  if (header.recordingId) lines.push(`Recording  ${printable(header.recordingId)}`);

  const signals = file.dataSignals;
  lines.push('');
  /*
    Both counts through `counted`, which this function already uses one line up for the record
    count and which exists so a count and its noun cannot disagree.

    The annotation-channel half was pluralised by hand when it was fixed — a file carrying two
    of them read "2 annotation channel", and EDF+ permits more than one — and the signal half
    had been hand-rolled since it was written. Two spellings of one rule in one line, in the
    file that imports the rule.
  */
  const annotationCount = file.annotationSignals.length;
  const annotationPart =
    annotationCount > 0 ? ` + ${counted(annotationCount, 'annotation channel')}` : '';
  lines.push(`Channels   ${counted(signals.length, 'signal')}${annotationPart}`);
  lines.push('');

  const rows: string[][] = [['#', 'COLUMN', 'LABEL', 'UNIT', 'RATE', 'RANGE', 'OUTPUT']];
  const fileFor = new Map<number, string>();
  for (const group of plan.groups) {
    for (const channel of group.channels) fileFor.set(channel.signal.index, group.fileName);
  }
  // Rendered as a group so that two channels recorded at different rates never show the
  // same figure in the RATE column, which is the one thing this table is asked to settle.
  const rateText = formatRates(signals.map((signal) => signal.samplingRate));
  for (const [row, signal] of signals.entries()) {
    rows.push([
      String(signal.index),
      printable(plan.columnNames.get(signal.index) ?? ''),
      printable(signal.label),
      printable(signal.physicalDimension),
      `${rateText[row]} Hz`,
      `${signal.physicalMin} to ${signal.physicalMax}`,
      /*
        A channel with no samples was reported as "(not selected)", which is a different
        thing and not true when it was named on --channels. `edf2csv rec.edf --info
        --channels unused` said the channel the command asked for had not been chosen, when
        what is actually the case is that the file gives it nothing to convert. The
        NO_SAMPLES warning below the table says so; the table contradicted it.
      */
      /*
        And under --annotations-only nothing was deselected either — the run writes no signal
        table at all, which is the same distinction one paragraph up.

        `--info --annotations-only --channels "EEG Fpz-Cz"` accepted the name, checked it
        against the file, and then printed "(not selected)" against the very channel it had
        just been asked for. The sentence three lines under the table already says the truth,
        "and no signal data", and `channels.csv` and `--info --json` both say it too — the
        cell is empty and `output_file` is null. The human table was the one place claiming a
        choice nobody made.
      */
      plan.writeSignals
        ? fileFor.has(signal.index)
          ? toStdout
            ? '(stdout)'
            : (fileFor.get(signal.index) as string)
          : signal.samplesPerRecord === 0
            ? '(no samples)'
            : '(not selected)'
        : '(no signal data)',
    ]);
  }
  /*
    A column head over nothing is not a table.

    The line above has just said `Channels   0 signals + 1 annotation channel`, and this then
    printed

        #  COLUMN  LABEL  UNIT  RATE  RANGE  OUTPUT

    with no row under it — seven headings for a set of no channels, on a recording holding
    only events, which is what `annotations-only.edf` in this repository is and what a
    companion event file out of a scoring system is. The same sentence `--channels "#0"` was
    given its own wording for: "No channel at position #0. This file has signal channels at
    ." states there are some and then names none.

    Nothing goes in its place. The count is stated one line up and the body three lines down
    says what the conversion would write; a table saying "none" twice more adds nothing.
  */
  if (signals.length > 0) {
    lines.push(table(rows, new Set([0])));
    lines.push('');
  }
  /*
    From here down --info stops laying out columns and starts explaining itself, so from
    here down it wraps. The key-value lines and the channel table above are aligned to each
    other and must not be re-flowed; these are sentences, and the longest of them ran to 156
    columns — which is not a line anyone reads, it is a line a terminal breaks somewhere.
    --help has been written to 80 since it existed and hints joined it in 0.7.1; --info is
    the mode whose whole purpose is being read by a person, and it was the last one guessing.
  */
  if (plan.groups.length > 1) {
    /*
      And the third thing this sentence could not say, after the OUTPUT column above it.

      `--stdout` on a recording with more than one rate is refused — it writes one table and
      this makes several — so no file is written and the run does not happen. The sentence
      described it anyway, three lines under a column saying `(stdout)` and six above the
      warning that the run would be refused:

          0  EEG Fpz-Cz  ...  256 Hz  -250 to 250  (stdout)
          Sampling rates differ, so channels are written to 3 files, one per rate.
          warning: --stdout would refuse this run: needs exactly one table, but this
                   recording produces 3 ...

      Three statements about one run, and the middle one names an outcome none of the others
      allows. What the rates decide is how many tables there are; where those go is the
      question this mode has already answered differently.
    */
    lines.push(
      wrap(
        plan.layout === 'long'
          ? `Sampling rates differ, and the long layout puts them in one table anyway: each row ` +
            `carries its own time, so nothing has to line up. No channel is resampled.`
          : toStdout
            ? `Sampling rates differ, so this recording makes ${counted(plan.groups.length, 'table')}, ` +
              `one per rate — more than --stdout can write. Converting into a directory writes one ` +
              `file each; --layout long puts them all in one table.`
            : `Sampling rates differ, so channels are written to ${counted(plan.groups.length, 'file')}, one per rate. ` +
              `No channel is resampled.`,
      ),
    );
  }
  /*
    The estimate describes the signal tables, and says so when that is not what will be
    written.

    Under --annotations-only there are no signal tables, and the line read "Would write 0
    rows, roughly 0 B." for a conversion that goes on to write annotations.csv with three
    events in it. --info exists to say what a conversion will do; asserting it will write
    nothing, when it will write a file, is the one thing it must not do.

    How many events there are cannot be answered from the header — the annotation channel has
    to be read record by record, which is the scan --info is for avoiding. So it says which
    file, and that the count is not knowable this cheaply, rather than inventing a zero.
  */
  /*
    No signal table to describe, whichever way that came about.

    This asked only whether `--annotations-only` had been given. A recording that has no
    signal channels — one holding nothing but EDF+ annotations — has none either, and fell
    through to the estimate line: "Would write 0 rows, roughly 0 B." for a conversion that
    goes on to write an annotations.csv with events in it, beside channels.csv and
    metadata.json. That is the sentence 0.4.51 removed, arriving by the other route.
  */
  if (!plan.writeSignals || plan.groups.length === 0) {
    // Named as they will be written. --info is read to find out what a run leaves behind,
    // and a script that opens the name it was given must find a file there.
    const suffix = plan.gzip ? '.csv.gz' : '.csv';
    const files =
      file.annotationSignals.length > 0
        ? events === null
          ? `annotations${suffix} and channels${suffix}, and no signal data`
          : `annotations${suffix} with ${counted(events, 'event')} and ` +
            `channels${suffix}, and no signal data`
        : `channels${suffix} and no signal data — and no annotations${suffix} ` +
          'either, since this recording has no annotation channel';
    // The one form of this line whose count is not in hand; see the `events` parameter.
    const unknown =
      file.annotationSignals.length > 0 && events === null
        ? ' How many events there are cannot be told from the header, and finding out means ' +
          'reading the annotation channel record by record.'
        : '';
    /*
      And the other half of what 0.9.34 corrected one branch down.

      Both of the refusals that land here are refusals: `--stdout` has nothing to stream for
      `--annotations-only`, and nothing for a recording with no signal channels. So this line
      named two files for a run that writes none, three lines above the warning saying the
      run will not happen and one of them above a hint offering the same two files as the
      thing to convert to a directory for:

          Would write annotations.csv and channels.csv, and no signal data.

          warning: --stdout would refuse this run: has no signal data to write: this
                   recording has no signal channels, only EDF+ annotations.
                   Convert to a directory to get its annotations.csv, or drop --stdout.

      Named as the conversion that does write them, which is the one the hint sends you to.
    */
    lines.push(
      wrap(
        stdoutRefused
          ? // Refusal first, since two of the three forms end in a subordinate clause that a
            // trailing one would have to be read past.
            `--stdout writes nothing here; converting into a directory would write ` +
            `${files}.${unknown}`
          : `Would write ${files}.${unknown}`,
      ),
    );
    return lines.join('\n');
  }

  // The estimate counts the characters of the CSV, which is what --gzip then compresses.
  // Reporting it as the size on disk would overstate a compressed conversion several-fold.
  const compressing = plan.gzip;
  /*
    And the events file, which this line named for every run except the ones that write it.

    The branch above names `annotations.csv` because those runs write nothing else; a recording
    with signals *and* events falls through to here, and here the sentence is about the signal
    tables alone. So `--info` on an ordinary EDF+ recording described three of the four files a
    conversion leaves behind and never mentioned the fourth — the one carrying the scoring,
    which is the reason most people convert an EDF+ file at all.

    "Named as they will be written", like the OUTPUT column and the branch above: `--gzip`
    changes the name, and `--stdout` writes no sidecar at all, so it says nothing there. The
    count is the one `--info` already has — read on a discontinuous file, and honestly absent
    on a continuous one, where finding it means the scan this mode exists to avoid.
  */
  const alsoEvents =
    !toStdout && file.annotationSignals.length > 0
      ? `, and ${outputCsvName('annotations', plan.gzip)}` +
        `${events === null ? '' : ` with ${counted(events, 'event')}`}`
      : '';
  // A window narrow enough to select one sample is an ordinary thing to ask for, and this
  // read "Would write 1 rows, roughly 22 B." — the slip 0.5.74 fixed on the lines above it
  // and missed here, because the recording that test builds never estimates exactly one.
  const size =
    `${counted(plan.estimate.rows, 'row')}, roughly ` +
    `${formatBytes(plan.estimate.bytes)}${compressing ? ' before compression' : ''}`;
  lines.push(
    wrap(
      stdoutRefused
        ? // "That conversion" where the paragraph above has just named the directory one,
          // which is every multi-rate refusal; a batch refusal reaches this line on a
          // single-rate recording too, and there the sentence has to name it itself.
          (plan.groups.length > 1 && plan.layout !== 'long'
            ? 'That conversion would write '
            : 'Converting into a directory would write ') + `${size}; --stdout writes none of them.`
        : `Would write ${size}${alsoEvents}.`,
    ),
  );

  return lines.join('\n');
}

/**
 * The `--info` view as JSON, for surveying files from a script.
 *
 * `indent` is 2 for a single recording, matching what this has always printed, and null for
 * a batch — several pretty-printed documents run together are readable by a streaming parser
 * but not by anything that expects one record per line, and a batch is exactly where
 * line-oriented reading is wanted. null rather than undefined because a default parameter
 * takes effect when undefined is passed, which quietly restored the indentation this was
 * meant to drop; JSON.stringify itself wants undefined, so it is translated at the call.
 *
 * `--info` answers "what is in this recording and what would converting it cost", which
 * is exactly the question you want to ask across a directory of hundreds of recordings —
 * and the text table is the wrong shape for that. `--json` previously applied only to
 * conversions, so scripts had to parse the aligned columns or convert files just to learn
 * what was in them.
 *
 * Field names match `metadata.json` where the two describe the same thing, so a survey and
 * a conversion can be read by the same code.
 */
export function infoJson(
  file: EdfFile,
  plan: ConversionPlan,
  /**
   * The warnings as `--info` prints them, which is not the list they start as.
   *
   * This built its own — `withoutFileRateWarning(file.diagnostics).concat(plan.diagnostics)` —
   * and so missed every amendment the text form makes on the way out. `--info --json --gzip`
   * said a label "will appear as the channel's name in signals.csv" for a run that writes
   * `signals.csv.gz`; `--stdout` named the same file for a run that writes no file at all;
   * `--annotations-only` promised a signal table it does not write, and `--channels` promised
   * one for a channel it leaves out. Thirteen sentences across the fixtures, all of them in
   * the surface a script reads, and the text beside them already correct.
   */
  warnings: readonly Diagnostic[],
  /**
   * The event count, on the files where `--info` has already read and counted them.
   *
   * `formatInfo` has taken this since 0.7.x and prints it — "Would write annotations.csv with
   * 3 events" — and the same number was dropped on the way to the JSON, which is the surface
   * a script reads and the reason `--info --json` exists. Null where the count is not in
   * hand, by the same rule `estimate` is null for a run that writes no signal table: a
   * continuous file is read only as far as its origin, so its events have not been counted
   * and there is no honest number to put here.
   */
  events: number | null = null,
  indent: number | null = 2,
  /** See `formatInfo`: under `--stdout` there is no file to name. */
  toStdout = false,
): string {
  const { header } = file;
  const fileFor = new Map<number, string>();
  for (const group of plan.groups) {
    for (const channel of group.channels) fileFor.set(channel.signal.index, group.fileName);
  }

  const document = JSON.stringify(
    {
      tool: TOOL,
      path: file.path,
      bytes: file.fileSize,
      format: describeFormat(header),
      start_datetime_local: formatWallClock(header.startDateTime),
      start_date_raw: header.startDateRaw,
      start_time_raw: header.startTimeRaw,
      patient_id: header.patientId,
      recording_id: header.recordingId,
      data_records: file.recordCount,
      data_records_declared: header.declaredRecordCount,
      record_duration_seconds: header.recordDuration,
      duration_seconds: file.durationSeconds,
      // For a discontinuous file this exceeds duration_seconds by the length of the gaps.
      time_span_seconds: plan.range.recordingEndSeconds - plan.range.recordingStartSeconds,
      // Where `time_s` begins, and the clock `--start` and `--end` are read against. Usually
      // zero; not when the first record's timekeeping TAL puts the recording elsewhere. Both
      // of the fields above are lengths and neither says where that length sits.
      first_sample_seconds: plan.range.recordingStartSeconds,
      /*
        The window, under the names `metadata.json` gives it, which is the rule this document
        follows for everything describing the run.

        The text form did not show it either until 0.8.82; here the only trace of `--start`
        and `--end` was `estimate.rows` coming back smaller, with nothing in the document
        saying why. A survey that runs `--info --json` over a folder with a window on the
        command line archives a description of each recording in which the window does not
        appear.
      */
      start_seconds: plan.range.startSeconds,
      end_seconds: plan.range.endSeconds,
      whole_recording: plan.range.isWholeRecording,
      annotation_channels: file.annotationSignals.length,
      // Named as `summaryJson` names the same count, so a prediction and a conversion read
      // the same field.
      annotations: events,
      channels: file.dataSignals.map((signal) => ({
        signal_index: signal.index,
        column: plan.columnNames.get(signal.index) ?? '',
        label: signal.label,
        unit: signal.physicalDimension,
        sampling_rate_hz: signal.samplingRate,
        samples_per_record: signal.samplesPerRecord,
        physical_min: signal.physicalMin,
        physical_max: signal.physicalMax,
        digital_min: signal.digitalMin,
        digital_max: signal.digitalMax,
        transducer: signal.transducer,
        prefiltering: signal.prefiltering,
        /*
          `-`, the placeholder `ConvertResult.outputDir` already uses for this mode, and not
          `null`: null is this document's word for "would not be converted", which is a
          different thing from "converted, to a stream".
        */
        output_file: fileFor.has(signal.index)
          ? toStdout
            ? '-'
            : (fileFor.get(signal.index) as string)
          : null,
      })),
      /*
        Null rather than zero when the run writes no signal table.

        The text form has refused to say "Would write 0 rows, roughly 0 B." since 0.4.51,
        because a run that goes on to write an annotations.csv with events in it has not
        written nothing — and it is `--annotations-only`, or a recording holding only
        annotations, that reaches this. The JSON went on saying it to the surface a script
        reads. There is no estimate for a table that does not exist, and null is how this
        document already says that.
      */
      estimate:
        plan.writeSignals && plan.groups.length > 0
          ? {
              rows: plan.estimate.rows,
              // Character count of the CSV. With --gzip the file on disk is smaller than this.
              bytes: plan.estimate.bytes,
              exceeds_spreadsheet_limit: plan.estimate.exceedsSpreadsheetLimit,
            }
          : { rows: null, bytes: null, exceeds_spreadsheet_limit: false },
      // The plan's mixed-rate warning replaces the header parser's, as it does everywhere
      // else. This was the one consumer left out of that when 0.3.2 made the warning follow
      // --channels, so `--info --json` carried it twice: once counting the rates being
      // converted and once counting every rate in the file, with the same code and severity.
      // The file's own first, then the plan's, which is the order the text form prints them
      // in. Concatenating the other way round listed the same warnings about the same
      // recording in two different sequences depending on which form you asked for.
      warnings: warnings.map((d) => ({ code: d.code, severity: d.severity, message: d.message })),
    },
    null,
    indent ?? undefined,
  );
  // A path is untrusted text on this surface too; see escapeJsonText.
  return escapeJsonText(document);
}

/**
 * One diagnostic per `warning: ` line, prefixed so warnings are greppable; the hint below it
 * wrapped to the terminal.
 *
 * The hint has been on its own unprefixed continuation line since these gained hints at all,
 * so grepping for `warning:` never picked it up and wrapping it costs nothing that was being
 * relied on — which is what 0.6.132 got wrong when it left every diagnostic long on the
 * grounds that they are one line each. Half of that is true. The `warning:` head is a line
 * per diagnostic and stays one, at whatever width the message runs to; the hint underneath
 * it is prose addressed to a person reading a terminal, and 17 of them ran past 80 columns,
 * the widest to 180. At that width the second half of the advice is wherever the terminal
 * decided to put it, indented under nothing, and the 9-space rule that says "this belongs to
 * the warning above" is lost at exactly the moment there is enough text for it to matter.
 */
export function formatDiagnostics(diagnostics: readonly Diagnostic[]): string {
  return diagnostics
    .map((d) => {
      // Diagnostics quote channel labels, which come from the file, so they need the
      // same treatment as the --info table.
      const head = `${d.severity}: ${printable(d.message)}`;
      return d.hint ? `${head}\n${wrap(printable(d.hint), HINT_INDENT)}` : head;
    })
    .join('\n');
}

export function formatSummary(result: ConvertResult): string {
  const lines: string[] = [];
  const rows: string[][] = [];
  for (const file of result.files) {
    // `.csv.gz` is still a CSV, and its rows are still rows. The suffix test dropped the
    // unit from every line of a --gzip summary, so the numbers stood on their own.
    rows.push([
      `  ${file.name}`,
      grouped(file.rows),
      // Singular at one, like every other count this prints: a one-row table is what a
      // narrow window produces, and "1 rows" is the same slip 0.5.74 fixed elsewhere.
      /\.csv(\.gz)?$/u.test(file.name) ? (file.rows === 1 ? 'row' : 'rows') : '',
    ]);
  }
  lines.push(`Wrote ${printable(result.outputDir)}`);  // Escaped; see the File line above.
  lines.push(table(rows, new Set([1])));
  lines.push(`Done in ${elapsed(result.elapsedMs)}.`);
  return lines.join('\n');
}

/**
 * How long the run took, in a unit it can be stated in.
 *
 * `elapsedMs` is a difference of two `Date.now()` readings, so it is whole milliseconds, and
 * `(ms / 1000).toFixed(1)` rounds everything under fifty of them to `0.0`. That is the last
 * line of every conversion of a small recording — `--annotations-only` on a three-record
 * file, every fixture in this repository, and the transcript on three documentation pages,
 * all of which read `Done in 0.0s.` for a run that read a file and wrote three.
 *
 * The same refusal `formatDuration` and `formatBytes` make one file over, and for the reason
 * stated there: none of them is a rounding rule, and each declines to print a form the
 * quantity cannot take — 1023.999 KB is not "1024 KB", two records of 1e-15s are not "0s",
 * and a conversion that happened did not take no time.
 *
 * A clock that reports no change at all has measured something below what it can resolve,
 * which is a different statement from zero and is what it says.
 */
export function elapsed(milliseconds: number): string {
  /*
    And the other end of it, which could only say seconds.

    This is the one place in the tool that states a length of time without `formatDuration`,
    and it is the line under a summary whose `Duration` field is printed *with* it. A
    conversion of an overnight recording read:

        Duration   8h 00m 0s  (28800 records of 1s)
        ...
        Done in 412.7s.

    Two lengths of time on one screen, one decomposed and one not, from the same program. 412
    seconds is a number a reader has to divide by sixty to hold, which is the whole reason
    `formatDuration` exists and the same complaint `grouped` makes about counts.

    Handed over at a minute, since below that the seconds are the readable form — and rounded
    to the tenth this already prints first, because `formatDuration` keeps three decimals and
    "6m 52.734s" claims a precision two `Date.now()` readings do not have.
  */
  /*
    Rounded to the precision that will be printed, and only then given a form.

    The branch was chosen at sixty thousand milliseconds and the seconds were rounded to one
    decimal afterwards, so the last fifty milliseconds below the handover rounded up through
    it: 59,950 to 59,999 printed `60.0s`, a minute stated in seconds, one millisecond under
    the line that exists so a minute is stated as a minute.

        Done in 60.0s.        59,999 ms
        Done in 1m 0s.        60,000 ms

    Which is the slip `formatBytes` fixed in the same words — "Rounding can carry into the
    next unit, and the unit was chosen before it: 1,048,575 bytes is 1023.999 KB, which
    printed as 1024 KB" — and the one the paragraph above cites as the rule this function
    follows. Rounding first is what `formatDuration` does before it splits into hours and
    minutes, for the same reason.
  */
  const tenths = Math.round(milliseconds / 100) / 10;
  if (tenths >= 60) return formatDuration(tenths);
  if (milliseconds >= 50) return `${tenths.toFixed(1)}s`;
  return milliseconds > 0 ? `${milliseconds}ms` : 'under 1ms';
}

/**
 * Which version produced this record.
 *
 * `metadata.json` has carried it since the file existed, because a conversion should be
 * reproducible later. The two JSON *streams* did not, and they are the ones most likely to
 * outlive the run: `--json` exists to be piped into something, logged, or committed beside a
 * result, where the question a year on is which release's field names and rounding these are.
 * The same shape as metadata.json's, so a consumer reads one field either way.
 */
const TOOL = { name: 'edf2csv', version: VERSION } as const;

export function summaryJson(result: ConvertResult, indent: number | null = 2): string {
  const document = JSON.stringify(
    {
      tool: TOOL,
      output_dir: result.outputDir,
      files: result.files,
      annotations: result.annotationCount,
      duration_seconds: result.file.durationSeconds,
      records: result.file.recordCount,
      elapsed_ms: result.elapsedMs,
      warnings: result.diagnostics.map((d) => ({ code: d.code, severity: d.severity, message: d.message })),
    },
    null,
    indent ?? undefined,
  );
  // A path is untrusted text on this surface too; see escapeJsonText.
  return escapeJsonText(document);
}
